import { useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router'
import { useAuth } from '../hooks/useAuth'
import { getQueryValue, normalizeVerificationCode } from '../utils/authFlowParams'
import { validatePassword } from '../lib/validatePassword'
import { AuthShell } from '../components/auth/AuthShell'
import {
  AuthFormCard,
  AuthSubmitButton,
  AuthFooterLink,
} from '../components/auth/AuthFormCard'
import { PasswordInput } from '../components/common'
import { PasswordRequirementsField } from '../components/auth/PasswordRequirementsField'

function ResetPasswordPage() {
  const navigate = useNavigate()
  const { resetPassword } = useAuth()
  const [searchParams] = useSearchParams()
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [submitted, setSubmitted] = useState(false)

  const userId = useMemo(
    () => getQueryValue(searchParams, ['user_id', 'userId', 'userid']),
    [searchParams]
  )
  const code = useMemo(
    () => normalizeVerificationCode(getQueryValue(searchParams, ['code', 'verification_code', 'verificationCode'])),
    [searchParams]
  )

  const handleSubmit = async (event) => {
    event.preventDefault()
    setError('')

    const validationError = validatePassword(newPassword, confirmPassword)
    if (validationError) {
      setError(validationError)
      return
    }
    if (!userId || !code) {
      setError('The reset link is missing required verification details')
      return
    }

    setLoading(true)
    try {
      await resetPassword(userId, code, newPassword)
      setSubmitted(true)
      navigate('/login', { replace: true, state: { passwordReset: true } })
    } catch (err) {
      setError(err.message || 'Failed to reset password')
    } finally {
      setLoading(false)
    }
  }

  return (
    <AuthShell>
      <AuthFormCard
        title="Choose a new password"
        subtitle="Finish your password reset and sign back in to Studio."
        onSubmit={handleSubmit}
        errorMessage={error}
        footer={<AuthFooterLink to="/login">Back to login</AuthFooterLink>}
      >
        <PasswordRequirementsField
          id="new_password"
          label="New Password"
          value={newPassword}
          onChange={setNewPassword}
          placeholder="Enter a strong password"
          required
          disabled={loading || submitted}
          autoFocus
          autoComplete="new-password"
        />

        <PasswordInput
          id="confirm_password"
          label="Confirm Password"
          value={confirmPassword}
          onChange={setConfirmPassword}
          placeholder="Re-enter your password"
          required
          disabled={loading || submitted}
          autoComplete="new-password"
          size="large"
        />

        <AuthSubmitButton
          loading={loading}
          submitted={submitted}
          loadingLabel="Updating..."
        >
          Reset password
        </AuthSubmitButton>
      </AuthFormCard>
    </AuthShell>
  )
}

export default ResetPasswordPage
